<template>
    <div class="mb-8">
        <div class="flex justify-between">
            <div>
                <h1 class="mb-8 font-bold text-sm lg:text-3xl">
                    <Link class="text-indigo-400 hover:text-indigo-600" :href="route('auctions')">{{$t('pages.auctions.info.auctions')}}</Link>
                    <span class="text-indigo-400 font-medium">/</span>
                    {{ auctionState.id }}
                </h1>
            </div>
            <div class="justify-end">
                <status v-bind:data="getAuctionState(auctionState)"/>
            </div>
        </div>
        <div class="flex flex-wrap">
            <div class="w-full xl:w-2/3 bg-white rounded rounded-r-none shadow overflow-hidden bg-gray-100 p-10 overflow-x-auto">
                <h2 class="px-0 md:px-1 lg:px-6 text-xl font-medium">{{$t('pages.auctions.info.bids')}} ({{ auctionState.bids.length }})</h2>
                <table class="w-full whitespace-no-wrap text-xs">
                    <tr class="text-left font-bold">
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.bids_columns.id')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.bids_columns.article_number')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.bids_columns.customer_id')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.bids_columns.username')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.bids_columns.amount')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.bids_columns.bid')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.bids_columns.submission_of_bid')}}</th>
                    </tr>
                    <tr @mouseenter="bidHover = bid.id" @mouseleave="bidHover = 0" v-for="bid in auctionState.bids" :key="bid.id" class="hover:bg-gray-200 focus-within:bg-gray-200 border-0" :class="{'border-green-600 border-l-4': bid.leading && markLeadingBids, 'border-red-400 border-l-4': !bid.leading && markLeadingBids, 'bg-gray-200': orderHover === bid.id}">
                        <td class="border-t"><a class="px-0 md:px-1 lg:px-6 py-4 flex items-center">{{ bid.id }}</a></td>
                        <td class="border-t"><a class="px-0 md:px-1 lg:px-6 py-4 flex items-center">{{ bid.product_id }}</a></td>
                        <td class="border-t"><a class="px-0 md:px-1 lg:px-6 py-4 flex items-center">{{ bid.customer_id }}</a></td>
                        <td class="border-t"><a class="px-0 md:px-1 lg:px-6 py-4 flex items-center">{{ bid.customer_name }}</a></td>
                        <td class="border-t"><a class="px-0 md:px-1 lg:px-6 py-4 flex items-center">{{ bid.quantity }}</a></td>
                        <td class="border-t"><a class="px-0 md:px-1 lg:px-6 py-4 flex items-center">{{ bid.amount | toCurrency(2) }}</a></td>
                        <td class="border-t"><a class="px-0 md:px-1 lg:px-6 py-4 flex items-center">{{ bid.created_at | formatDate }}</a></td>
                    </tr>
                    <tr v-if="auctionState.bids.length === 0">
                        <td class="border-t px-1 md:px-6 py-4" colspan="6">{{$t('pages.auctions.info.no_bids')}}</td>
                    </tr>
                </table>
            </div>
            <div class="w-full xl:w-1/3 bg-white rounded rounded-l-none overflow-hidden bg-gray-100 p-10">
                <auction-data-viewer
                    :auction="auctionState"
                    :runtime="runtime"
                    :fields="[]"
                />
            </div>
            <div class="w-full bg-white rounded rounded-r-none shadow overflow-hidden bg-gray-100 p-10 overflow-x-auto">
                <h2 class="px-0 md:px-1 lg:px-6 text-xl font-medium">{{$t('pages.auctions.info.orders')}} ({{ auctionState.orders.length }})</h2>
                <table class="w-full whitespace-no-wrap text-xs">
                    <tr class="text-left font-bold" v-if="!auctionState.running">
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.order_columns.id')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.order_columns.bid_id')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.order_columns.article_number')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.order_columns.customer_id')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.order_columns.quantity')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.order_columns.amount')}}</th>
                        <th class="px-1 md:px-2 lg:px-6 py-4">{{$t('pages.auctions.info.order_columns.order_creation')}}</th>
                    </tr>
                    <tr @mouseenter="orderHover = order.bid_id" @mouseleave="orderHover = 0" v-for="order in auctionState.orders" :key="order.id" class="hover:bg-gray-100 focus-within:bg-gray-100" v-if="!auction.running" :class="{ 'bg-gray-200': bidHover === order.bid_id}">
                        <td class="border-t"><a class="px-1 md:px-2 lg:px-6 py-4 flex items-center">{{ order.id }}</a></td>
                        <td class="border-t"><a class="px-1 md:px-2 lg:px-6 py-4 flex items-center">{{ order.bid_id }}</a></td>
                        <td class="border-t"><a class="px-1 md:px-2 lg:px-6 py-4 flex items-center">{{ order.product_id }}</a></td>
                        <td class="border-t"><a class="px-1 md:px-2 lg:px-6 py-4 flex items-center">{{ order.customer_id }}</a></td>
                        <td class="border-t"><a class="px-1 md:px-2 lg:px-6 py-4 flex items-center">{{ order.quantity }}</a></td>
                        <td class="border-t"><a class="px-1 md:px-2 lg:px-6 py-4 flex items-center">{{ order.amount | toCurrency(2) }}</a></td>
                        <td class="border-t"><a class="px-1 md:px-2 lg:px-6 py-4 flex items-center">{{ order.created_at | formatDate }}</a></td>
                    </tr>
                    <tr v-if="auctionState.orders.length === 0">
                        <td class="border-t px-1 md:px-6 py-4" colspan="6" v-if="!auctionState.running">{{$t('pages.auctions.info.no_orders')}}</td>
                    </tr>
                </table>
                <div class="bg-teal-100 border-t-4 border-teal-500 rounded-b text-teal-900 px-4 py-3 shadow-md mx-0 md:mx-6 mt-2" role="alert" v-if="auctionState.running">
                    <div class="flex">
                        <div class="py-1"><svg class="fill-current h-6 w-6 text-teal-500 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"/></svg></div>
                        <div>
                            <p class="font-bold">{{$t('pages.auctions.info.auction_still_running')}}</p>
                            <p class="text-sm">{{$t('pages.auctions.info.therefore_no_order_creation')}}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Layout from '@/Shared/Layout';
    import AuctionDataViewer from "@/Shared/AuctionDataViewer";
    import Icon from '@/Shared/Icon';
    import PageHeadline from "@/Shared/PageHeadline";
    import Status from '@/Shared/Status';
    import AuctionState from "@/mixins/AuctionState";
    import { Link } from '@inertiajs/vue2';
    export default {
        metaInfo() {
            return {
                title: this.$t('pages.auctions.info.auction') + ` ${this.form.id}`
            }
        },
        mixins: [AuctionState],
        layout: Layout,
        components: {
            AuctionDataViewer,
            Icon,
            PageHeadline,
            Status,
            Link
        },
        props: {
            auction: Object,
            runtime: Object
        },
        data() {
            return {
                form: {
                    id: this.auction.id
                },
                bidHover: 0,
                orderHover: 0
            }
        },
        computed: {
            markLeadingBids() {
                return [1,2].includes(this.auctionState.auction_type_id);
            },
            auctionState() {
                return {
                    ...this.auction,
                    shop_price: parseFloat(this.auction.attributes?.shop_price),
                    start_price: parseFloat(this.auction.start_price),
                    pricedrop_amount: parseFloat(this.auction.pricedrop_amount),
                    article_quantity: parseInt(this.auction.article_quantity),
                    amount_bid_max: parseFloat(this.auction.amount_bid_max),
                    amount_bid_min: parseFloat(this.auction.amount_bid_min),
                    attributes: this.auction.attributes ? {
                        lfd_nr: parseInt(this.auction.attributes.lfd_nr ? this.auction.attributes.lfd_nr : 0),
                        reference_price: parseFloat(this.auction.attributes.reference_price ? this.auction.attributes.reference_price : 0),
                        category: this.auction.attributes.category ? this.auction.attributes.category : false,
                        highlight: this.auction.attributes.highlight ? this.auction.attributes.highlight : false
                    } : {}
                };
            },
        }
    }
</script>
